import { useState } from 'react';
import { AuthProvider, useAuth } from '@/auth/AuthContext';
import { PlaygroundShell, type NavSection } from '@/components/PlaygroundShell';
import { SignInGate } from '@/components/SignInGate';
import type { Mode, PlaygroundConfig } from '@/types';
import { GROUPS, PLAYGROUNDS } from '@/playgrounds';

export default function App() {
  const config: PlaygroundConfig = window.libertifyPlaygroundConfig || { env: 'production' };
  return (
    <AuthProvider initialEnv={config.env}>
      <Router />
    </AuthProvider>
  );
}

function Router() {
  // Default to the first guided flow — most developers will discover the
  // playground for the first time and want a happy path, not a single
  // endpoint.
  const [activeId, setActiveId] = useState<string>(PLAYGROUNDS[0].id);
  const [mode, setMode] = useState<Mode>('preview');
  const { tokens } = useAuth();

  const navSections: NavSection[] = GROUPS.map((g) => ({
    id: g.id,
    label: g.label,
    items: PLAYGROUNDS.filter((p) => p.group === g.id).map((p) => ({
      id: p.id,
      label: p.label,
      description: p.description,
      status: p.status,
    })),
  })).filter((s) => s.items.length > 0);

  const active = PLAYGROUNDS.find((p) => p.id === activeId) || PLAYGROUNDS[0];
  const Component = active.component;

  // Force preview mode when the user lands but isn't signed in — sign in
  // gate appears beneath every playground until tokens are available.
  const effectiveMode: Mode = mode === 'live' && !tokens ? 'preview' : mode;
  const isFlow = active.group === 'flows';

  return (
    <PlaygroundShell
      navSections={navSections}
      activeId={active.id}
      onSelect={setActiveId}
      mode={mode}
      onModeChange={setMode}
    >
      {/* Flows always require a token — they need to call /api/users/me/organizations
          right away. Single endpoints can be inspected in preview mode. */}
      {(mode === 'live' || isFlow) && !tokens ? (
        <SignInGate>
          <Component mode={effectiveMode} />
        </SignInGate>
      ) : (
        <Component mode={effectiveMode} />
      )}
    </PlaygroundShell>
  );
}
